/* eslint-disable react/prop-types */
import loadable from '@loadable/component';
import classnames from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useFrontload } from 'react-frontload';
import useMediaQuery from '../../utils/useMediaQuery';
import getCDNImage from '../../utils/getCDNImage';

import styles from './RelatedProductsList.module.scss';
import { getValidResponseBody, taxiApi } from '../../api';
import { useEffect } from 'react';

const Product = loadable(() => import('../Product/Product'));
const Sliders = loadable(() => import('../Sliders/Sliders'));

const getRelatedProducts = async (EAN, id, isExpiredProduct, taxiApi) => {
  return await taxiApi.getRelatedPricesData(isExpiredProduct, EAN, id);
};

//@params: {EAN, id, isExpiredProduct} || { productsList }
//Pass first type of params to fetch related products by current product id
//Pass second type to pass existing products list to render
const useRelatedProducts = ({ EAN, id, isExpiredProduct, productsList }) => {
  const { data: frontloadRelatedProductsData, frontloadMeta, setData } = useFrontload(
    'related-products',
    async ({ taxiApi }) => ({
      //if products list is already in props, we'll use it. Otherwise we'll get it with api call
      relatedProducts: productsList ? null : await getRelatedProducts(EAN, id, isExpiredProduct, taxiApi),
    })
  );

  useEffect(() => {
    //if products list already in props, no need to fetch products
    if (productsList) return;

    (async () => {
      const relatedProducts = await getRelatedProducts(EAN, id, isExpiredProduct, taxiApi);
      setData(() => ({ relatedProducts }));
    })();
  }, [EAN, id, isExpiredProduct]);

  if (productsList) return productsList;

  if (frontloadMeta.pending || !frontloadRelatedProductsData?.relatedProducts?.ok) return null;

  return getValidResponseBody(frontloadRelatedProductsData?.relatedProducts);
};

const RelatedProductsList = ({ EAN, id, isExpiredProduct, productsList, classList = {} }) => {
  const { isGreaterOrEqualTo, isLowerThan, BREAKPOINTS } = useMediaQuery();
  const showProductsOnExtraLargeDisplay = 6;

  const dataRelatedProducts = useRelatedProducts({ EAN, id, isExpiredProduct, productsList });

  if (!dataRelatedProducts?.length) return null;

  const arrayOnExtraLargeDisplay =
    dataRelatedProducts?.length >= showProductsOnExtraLargeDisplay
      ? dataRelatedProducts.slice(0, 5)
      : dataRelatedProducts;

  return (
    <section className={classnames(styles.root)}>
      <h2 className={classnames(classList.title || styles.title)}>
        <FormattedMessage default="Related" id="common.related_products_1" />{' '}
        <b>
          <FormattedMessage default="products" id="common.related_products_2" />
        </b>
      </h2>

      {isGreaterOrEqualTo(BREAKPOINTS.EXTRA_EXTRA_LARGE) && (
        <div className={classnames(styles.wrappBigScreen)}>
          {arrayOnExtraLargeDisplay.map((product, index) => (
            <Product
              discountPercent={product?.discount_pct}
              classList={{ root: styles.boxShadow }}
              key={index}
              id={product.id}
              shop={product.campaign}
              url={product.canonical_path}
              name={product.name}
              price={product.price}
              originalPrice={product.price_original || product.price}
              shortDesc={product.shortDesc}
              image={getCDNImage(product?.thumbs?.[0])}
            />
          ))}
        </div>
      )}

      {isLowerThan(BREAKPOINTS.EXTRA_EXTRA_LARGE) && (
        <Sliders
          classList={{
            card: classnames(styles.sliderCard),
            root: styles.slidersRoot,
            color: styles.navBackground,
          }}
          gap={0}
          viewAll={false}
        >
          {dataRelatedProducts?.length > 0 &&
            dataRelatedProducts.map((product, index) => (
              <Product classList={{ root: styles.boxShadow }} key={index} product={product} />
            ))}
        </Sliders>
      )}
    </section>
  );
};

export default RelatedProductsList;
